import { css } from 'styled-components';
import { style, ResponsiveValue } from 'styled-system';

export interface TruncateProps {
  linesToShow: ResponsiveValue<number>;
  lineHeight: ResponsiveValue<number>;
}

/** @description [linesToShow, lineHeight] (only used internally) */
type TruncateVal = [number, number];

// helper function that combines two arrays similar to ramdas 'zip' function,
// but instead of truncating to the smaller ones size, keeps the size of the
// bigger array
const combineArrs = (arr1: number[], arr2: number[]) => {
  const getAtIndexOrLast = (idx: number, arr: number[]) => arr[idx] ?? arr[arr.length - 1]!;
  return (arr1.length > arr2.length ? arr1 : arr2).map<[number, number]>((_, idx) => [
    getAtIndexOrLast(idx, arr1),
    getAtIndexOrLast(idx, arr2),
  ]);
};

const maxHeight = style({
  prop: 'truncateVal',
  cssProperty: 'max-height',
  transformValue: (v: TruncateVal) => {
    const [linesToShow = 1, lineHeight = 1.4] = v;
    return `${lineHeight * linesToShow}rem`;
  },
});

const lineHeight = style({
  prop: 'truncateVal',
  cssProperty: 'lineHeight',
  transformValue: (v: TruncateVal) => {
    const [, lineHeight] = v;
    return `${lineHeight}rem`;
  },
});

const lineClamp = style({
  prop: 'truncateVal',
  cssProperty: '-webkit-line-clamp',
  transformValue: (v: TruncateVal) => {
    const [linesToShow = 1] = v;
    return `${linesToShow}`;
  },
});

/**
 * @description To improve in the future: it would be nicer not to use this
 * 'convertProps' function, but rather create a custom parser or something
 * along these lines instead. Also padding should also be taken into
 * consideration - this might break the line-height
 */
export const truncate = () => {
  // helper function with side-effet: combines 'linesToShow' and 'lineHeight'
  // into an array of tuples and assigns the result to the input argument
  const convertProps = (props: any) => {
    if (!props?.truncateVal) {
      const toArray = (val: any) => (Array.isArray(val) ? val : [val]);

      const linesToShow = props.linesToShow || 1;
      const lineHeight = props.lineHeight || 1.2;

      props.truncateVal = combineArrs(toArray(linesToShow), toArray(lineHeight));
    }

    return '';
  };

  const wordBreak = style({
    prop: 'linesToShow',
    cssProperty: 'word-break',
    transformValue: (linesToShow) => (linesToShow > 1 ? 'break-word' : 'break-all'),
  });

  return css`
    ${convertProps}
    ${maxHeight}
    display: block; /* Fallback for non-webkit */
    display: -webkit-box;
    ${lineHeight}
    ${lineClamp}
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre-wrap;
    ${wordBreak}
  ` as any;
};
